import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiClientError, downloadExport, downloadSlotsExport } from '../../lib/api';
import { addMonthsToKey, firstOfMonth, lastOfMonth, monthKey } from '../../lib/months';
import { todayInZurich } from '../../lib/today';
import { useDateRange } from '../../lib/useDateRange';
import { DateRangePicker } from '../../components/DateRangePicker';

export function AdminExportTab() {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="export-title" className="max-w-xl space-y-4">
      <h1 id="export-title" className="text-lg font-medium">
        {t('admin.export.title')}
      </h1>

      <ExportCard
        title={t('admin.export.bookingsTitle')}
        help={t('admin.export.bookingsHelp')}
        downloadLabel={t('admin.export.downloadBookings')}
        download={downloadExport}
      />

      <ExportCard
        title={t('admin.export.slotsTitle')}
        help={t('admin.export.slotsHelp')}
        downloadLabel={t('admin.export.downloadSlots')}
        download={downloadSlotsExport}
      />
    </section>
  );
}

/**
 * One self-contained download card: from/to inputs + a single primary button.
 * Each card owns its own date range so the warden can ask for different
 * windows on the two downloads in the same session.
 */
function ExportCard({
  title,
  help,
  downloadLabel,
  download,
}: {
  title: string;
  help: string;
  downloadLabel: string;
  download(from: string, to: string): Promise<void>;
}) {
  const { t } = useTranslation();
  const today = todayInZurich();
  const { from, to, reset } = useDateRange({
    initialFrom: firstOfMonth(addMonthsToKey(monthKey(today), -1)),
    initialTo: lastOfMonth(monthKey(today)),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trigger = async () => {
    setBusy(true);
    setError(null);
    try {
      await download(from, to);
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
      else setError(t('errors.ERR_INTERNAL'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 space-y-3">
      <h2 className="text-base font-medium">{title}</h2>
      <p className="text-sm text-slate-600 dark:text-slate-400">{help}</p>
      <div>
        <label className="text-sm min-w-0">
          <span className="block mb-1 text-slate-600 dark:text-slate-400">
            {t('admin.calendar.from')} – {t('admin.calendar.to')}
          </span>
          <DateRangePicker from={from} to={to} onChange={reset} />
        </label>
      </div>
      {error && (
        <p
          role="alert"
          className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={() => void trigger()}
        disabled={busy || from > to}
        className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-4 py-2 text-sm font-medium disabled:opacity-60"
      >
        {busy ? t('common.loading') : downloadLabel}
      </button>
    </div>
  );
}
